import { Component, ChangeDetectionStrategy, OnInit, ViewChild, TemplateRef, AfterViewInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PaginatorModule } from 'primeng/paginator';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { DropdownComponent } from '@app/components/dropdown/dropdown.component';
import { DialogModule } from 'primeng/dialog';
import {
  ListTableFiltersWrapperParent
} from '@app/wrappers/pages/list-table-filters-wrapper/list-table-filters-wrapper-parent';
import { UserTicket, UserTicketService, SystemInfoService, LocalizedModuleInfo, LocalizationString } from '@6grain/masofa-monolith-api-client/common';
import { IMsfFormBuilder } from "@root/src/app/components/form-builder/form-builder";
import { ListTableFiltersWrapper } from '@app/wrappers/pages/list-table-filters-wrapper/list-table-filters-wrapper';
import { RequestParams } from '@components/select/resource-component';
import { Router } from '@angular/router';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { TableColumn } from '@components/data-table/data-table.component';
import { DatePipe } from '@angular/common';
import { UserProfileService } from '@services/user-profile.service';
import { MessageService } from 'primeng/api';
import { buildFilterQuery } from '@app/utils/_msf';
import { SortType } from '@6grain/masofa-monolith-api-client/crop-monitoring/model/sortType';
import {environment} from '@root/src/environments/environment';

@Component({
  selector: 'app-user-support-tickets-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    TagModule,
    PaginatorModule,
    FormsModule,
    CheckboxModule,
    CardModule,
    DatePickerModule,
    SelectModule,
    DropdownComponent,
    ListTableFiltersWrapper,
    DateFormatLangPipe,
    DialogModule
  ],
  providers: [DatePipe, DateFormatLangPipe],
  templateUrl: './user-support-tickets-page.component.html',
  styleUrl: './user-support-tickets-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSupportTicketsPageComponent extends ListTableFiltersWrapperParent<UserTicket, UserTicketService> implements OnInit, AfterViewInit, OnDestroy {
  override filtersForms: IMsfFormBuilder[] = [];
  override getAllMethod = 'commonUserTicketGetByQueryPost'
  override getTotalMethod = 'commonUserTicketGetTotalCountPost'
  override storageKey = 'user_support_tickets_list_table'

  @ViewChild('createAt', { static: true }) createAt!: TemplateRef<any>;
  @ViewChild('moduleNameTemplate', { static: true }) moduleNameTemplate!: TemplateRef<any>;
  @ViewChild('statusTemplate', { static: true }) statusTemplate!: TemplateRef<any>;
  @ViewChild('actionsTemplate', { static: true }) actionsTemplate!: TemplateRef<any>;

   defaultParams: RequestParams = {
    take: 10,
    offset: 0,
    sortBy: 'CreateAt',
    sort: SortType.NUMBER_1,
    filters: []
  }

  tableColumns: TableColumn[] = [];

  public service = inject(UserTicketService);
  public tr = inject(TranslateService);
  private router = inject(Router);
  private userProfileService = inject(UserProfileService);
  private messageService = inject(MessageService);
  private systemInfoService = inject(SystemInfoService);
  private cdr = inject(ChangeDetectorRef);

  // Modal properties
  showCreateTicketModal = false;
  newTicket = {
    moduleName: '',
    exceptionJson: ''
  };

  // Dropdown options
  moduleOptions: { label: string; value: string }[] = [];
  private localizedModules: LocalizedModuleInfo[] = []; // Store original localized modules for translation updates
  userBaseFilters: Object[] = [];
  private langChangeSubscription: any;

  constructor() {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();

    // Add filters for user's own tickets by user ID
    const userProfile = this.userProfileService.getProfile();
    if (userProfile?.id) {
      const userFilters = buildFilterQuery({
        CreateUser: userProfile.id
      });
      // Store the initial user filter so we can merge it with additional filters
      this.userBaseFilters = userFilters;
      this.filtersParams = userFilters;
    }

    // Load dropdown options from backend
    this.loadModuleOptions();

    // Subscribe to language changes to update module translations
    this.langChangeSubscription = this.tr.onLangChange.subscribe(() => {
      this.updateModuleTranslations();
      // Force change detection to update table with new translations
      this.cdr.markForCheck();
    });

    this.filtersForms = [
      {
        type: 'select',
        title: this.tr.instant('Status'),
        placeholder: this.tr.instant('Status'),
        name: 'ticketStatus',
        selectOptions: this.getStatusOptionsWithLocalization(),
        options: {
          select: {
            optionLabel: 'label',
            optionValue: 'value',
            translateBy: 'names'
          }
        }
      },
      {
        type: 'input',
        title: this.tr.instant('Module Name'),
        placeholder: this.tr.instant('Module Name'),
        name: 'moduleName'
      }
    ];
  }

  ngAfterViewInit() {
    this.tableColumns = [
      { field: '', header: this.tr.instant('Date / Time'), type: 'custom', customTemplate: this.createAt },
      { field: '', header: this.tr.instant('Module Name'), type: 'custom', customTemplate: this.moduleNameTemplate },
      { field: '', header: this.tr.instant('Status'), type: 'custom', customTemplate: this.statusTemplate },
      { field: '', header: '', type: 'custom', customTemplate: this.actionsTemplate }
    ];
  }

  goToChat(row: any) {
    this.router.navigate([`/help/support-tickets/chat/${row.id}`]);
  }

  // Override applyFilters to merge base user filters with user-applied filters
  override applyFilters() {
    this.toggleFilter = !this.toggleFilter;
    const filters = {...this.filterModel};
    const userFilters = buildFilterQuery(filters);
    // Merge base user filters with applied filters
    this.filtersParams = [...this.userBaseFilters, ...userFilters];
  }

  // Override clearFilters to keep base user filters
  override clearFilters() {
    this.filterModel = {};
    this.toggleFilter = !this.toggleFilter;
    // Restore only base user filters, clear user-applied filters
    this.filtersParams = [...this.userBaseFilters];
  }

  getStatusLabel(status: number): string {
    switch (status) {
      case 0: return this.tr.instant('New');
      case 1: return this.tr.instant('In Progress');
      case 2: return this.tr.instant('Rejected');
      case 3: return this.tr.instant('Complete');
      default: return this.tr.instant('Unknown');
    }
  }

  getStatusSeverity(status: number): string {
    switch (status) {
      case 0: return 'info';
      case 1: return 'warning';
      case 2: return 'danger';
      case 3: return 'success';
      default: return 'secondary';
    }
  }

  // Get status options with LocalizationString for filter dropdown
  private getStatusOptionsWithLocalization(): Array<{ value: number; label: string; names: LocalizationString }> {
    return [
      {
        value: 0,
        label: 'New',
        names: {
          'ru-RU': 'Новый',
          'en-US': 'New',
          'uz-Latn-UZ': 'Yangi',
          'uz-Cyrl-UZ': 'Янги',
          'ar-LB': 'جديد'
        } as LocalizationString
      },
      {
        value: 1,
        label: 'In Progress',
        names: {
          'ru-RU': 'В работе',
          'en-US': 'In Progress',
          'uz-Latn-UZ': 'Jarayonda',
          'uz-Cyrl-UZ': 'Жараёнда',
          'ar-LB': 'قيد التنفيذ'
        } as LocalizationString
      },
      {
        value: 2,
        label: 'Rejected',
        names: {
          'ru-RU': 'Отклонено',
          'en-US': 'Rejected',
          'uz-Latn-UZ': 'Rad etilgan',
          'uz-Cyrl-UZ': 'Рад этилган',
          'ar-LB': 'مرفوض'
        } as LocalizationString
      },
      {
        value: 3,
        label: 'Complete',
        names: {
          'ru-RU': 'Завершено',
          'en-US': 'Complete',
          'uz-Latn-UZ': 'Tugallangan',
          'uz-Cyrl-UZ': 'Тугалланган',
          'ar-LB': 'مكتمل'
        } as LocalizationString
      }
    ];
  }

  openCreateTicketModal() {
    this.showCreateTicketModal = true;
    this.resetNewTicket();
  }

  closeCreateTicketModal() {
    this.showCreateTicketModal = false;
    this.resetNewTicket();
  }

  resetNewTicket() {
    this.newTicket = {
      moduleName: '',
      exceptionJson: ''
    };
  }

  createTicket() {
    if (!this.newTicket.moduleName) {
      this.messageService.add({
        severity: 'warn',
        summary: this.tr.instant('Warning'),
        detail: this.tr.instant('Please fill in all required fields')
      });
      return;
    }

    const userProfile = this.userProfileService.getProfile();
    if (!userProfile) {
      this.messageService.add({
        severity: 'error',
        summary: this.tr.instant('Error'),
        detail: this.tr.instant('User profile not found')
      });
      return;
    }

    this.loading = true;

    const ticketData: Partial<UserTicket> = {
      createUserName: userProfile.userName,
      createUserEmail: userProfile.email,
      moduleName: this.newTicket.moduleName,
      exceptionType: 'General Error', // Default value since we removed the dropdown
      exceptionJson: this.newTicket.exceptionJson || '{}',
      userDeviceType: 'Web Browser', // Fixed value for web version
      userDeviceJson: '{}', // Empty JSON for web version
      status: 0
    };

    // Add initialMessage to pass to backend handler
    if (this.newTicket.exceptionJson) {
      (ticketData as any).initialMessage = this.newTicket.exceptionJson;
      (ticketData as any).emailId = userProfile.id;
    }

    this.service.commonUserTicketCreatePost(ticketData as any).subscribe({
      next: (response: any) => {
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: this.tr.instant('Success'),
          detail: this.tr.instant('Ticket created successfully')
        });
        this.closeCreateTicketModal();
        // Refresh the table by reloading the page or trigger a reload
        window.location.reload();
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error creating ticket:', error);

        this.messageService.add({
          severity: 'error',
          summary: this.tr.instant('Error'),
          detail: this.tr.instant('Failed to create ticket. Please try again.')
        });
      }
    });
  }

  // Load module options from backend with localization
  private loadModuleOptions(): void {
    this.systemInfoService.commonSystemInfoGetAvailableModulesLocalizedGet().subscribe({
      next: (modules: LocalizedModuleInfo[]) => {
        this.localizedModules = modules;
        this.updateModuleTranslations();
      },
      error: (error: any) => {
        console.error('Error loading modules:', error);
        if (environment.showAlertLevel.Error) {
          this.messageService.add({
            severity: 'error',
            summary: this.tr.instant('Error'),
            detail: this.tr.instant('Failed to load modules')
          });
        }
        // Fallback to default modules without localization
        this.localizedModules = [
          { moduleName: 'Authentication', localizedNames: undefined },
          { moduleName: 'Dashboard', localizedNames: undefined },
          { moduleName: 'Reports', localizedNames: undefined },
          { moduleName: 'User Management', localizedNames: undefined },
          { moduleName: 'Settings', localizedNames: undefined },
          { moduleName: 'Other', localizedNames: undefined }
        ];
        this.updateModuleTranslations();
      }
    });
  }

  // Update module option labels with current language translations
  private updateModuleTranslations(): void {
    const currentLang = this.getCurrentLanguageKey();

    this.moduleOptions = this.localizedModules
      .filter(module => module.moduleName) // Filter out modules without name
      .map(module => {
        const localizedName = this.getLocalizedModuleName(module, currentLang);
        return {
          label: localizedName,
          value: module.moduleName!
        };
      });
  }

  // Get localized module name for current language
  private getLocalizedModuleName(module: LocalizedModuleInfo, languageKey: string): string {
    if (!module.localizedNames || !module.moduleName) {
      return module.moduleName || 'Unknown';
    }

    // First, try to get value from direct properties (e.g., localizedNames['ru-RU'])
    let names: any = module.localizedNames;

    // Check if direct property exists for current language
    const directValue = names[languageKey];
    if (directValue && typeof directValue === 'string' && directValue.trim()) {
      return directValue;
    }

    // If not found and valuesJson exists, try parsing it
    if (module.localizedNames.valuesJson && typeof module.localizedNames.valuesJson === 'string') {
      try {
        const parsed = JSON.parse(module.localizedNames.valuesJson);
        if (parsed && typeof parsed === 'object') {
          names = parsed;
          const parsedValue = names[languageKey];
          if (parsedValue && typeof parsedValue === 'string' && parsedValue.trim()) {
            return parsedValue;
          }
        }
      } catch (e) {
        console.warn('Failed to parse valuesJson:', e);
      }
    }

    // Fallback to other languages in order of preference
    const fallbackLanguages = ['ru-RU', 'en-US', 'uz-Latn-UZ', 'uz-Cyrl-UZ', 'ar-LB'];
    for (const lang of fallbackLanguages) {
      const value = names[lang];
      if (value && typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    // Final fallback to module name
    return module.moduleName;
  }

  // Get localized module name by module name string (for table display)
  getLocalizedModuleNameByName(moduleName: string | null | undefined): string {
    if (!moduleName) {
      return 'Unknown';
    }

    const module = this.localizedModules.find(m => m.moduleName === moduleName);
    if (!module) {
      return moduleName; // Fallback to original name if not found
    }

    const currentLang = this.getCurrentLanguageKey();
    return this.getLocalizedModuleName(module, currentLang);
  }

  // Get current language key in format expected by API (ru-RU, en-US, etc.)
  private getCurrentLanguageKey(): string {
    // TranslateService already uses full format: 'ru-RU', 'en-US', 'uz-Latn-UZ', 'ar-SA'
    const currentLang = this.tr.currentLang || this.tr.defaultLang || 'en-US';

    // If currentLang is already in API format, return it directly
    // Map 'ar-SA' to 'ar-LB' for API compatibility
    if (currentLang === 'ar-LB') {
      return 'ar-LB';
    }

    // Return as-is if it's already in the correct format
    return currentLang;
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
}
